/**
 * Transform backend error messages into user-friendly messages
 *
 * Handles:
 * - Confirmed errors from LicensesController.php and NewLicenseNumber.php validation
 * - Actual METRC API errors from production metrc_error_logs
 *
 * @param {string|any} backendMessage - The error message from the backend
 * @returns {string} - A user-friendly error message
 */
export function getFriendlyErrorMessage(backendMessage) {
  if (!backendMessage) {
    return "An unexpected error occurred. Please try again or contact support.";
  }

  const msg = String(backendMessage).toLowerCase();

  // METRC authorization errors (actual error from production logs)
  if (
    (msg.includes("authorization") && msg.includes("denied")) ||
    (msg.includes("api key") &&
      (msg.includes("not valid") ||
        msg.includes("invalid") ||
        msg.includes("missing"))) ||
    msg.includes("unauthorized")
  ) {
    return "Your METRC API credentials are invalid or expired. Please update your API credentials and try again.";
  }

  // License not in state system (reported by users in tickets)
  if (
    msg.includes("license") &&
    (msg.includes("not in") || msg.includes("not found")) &&
    (msg.includes("state") || msg.includes("table"))
  ) {
    return "This license number isn't in the state system yet. Please contact support for assistance.";
  }

  // Controller errors - these are definitive from LicensesController.php
  if (msg === "request failed") {
    return "We couldn't connect to METRC. Please check your API credentials or try again later.";
  }

  // Validation errors - these are definitive from NewLicenseNumber.php
  if (msg === "failed to validate license number in metrc") {
    return "We couldn't verify this license with METRC. Please check your API credentials.";
  }

  if (msg === "invalid license number") {
    return "This license number wasn't found in your METRC account. Please verify the license number.";
  }

  if (
    msg.includes("already a saved facility") &&
    msg.includes("license number")
  ) {
    return "This facility has already been added to your account.";
  }

  if (msg === "failed to save facility data") {
    return "We encountered an issue saving your facility. Please try again or contact support.";
  }

  if (msg === "failed to update facility data") {
    return "We encountered an issue updating your facility. Please try again or contact support.";
  }

  // METRC entity not found
  if (msg === "requested metrc entity not found") {
    return "This item wasn't found in METRC. Please verify the information and try again.";
  }

  // Keep user-friendly messages as-is
  const technicalTerms = [
    "exception",
    "throwable",
    "stack",
    "null",
    "undefined",
  ];
  const hasTechnicalJargon = technicalTerms.some((term) => msg.includes(term));

  if (!hasTechnicalJargon && backendMessage.length < 150) {
    return backendMessage;
  }

  return "We encountered an issue. Please try again or contact support.";
}
